import { useState } from 'react';

import { LookupAutocompleteView } from '@/shared/ui/molecules/LookupAutocomplete';

import { useBannerClassStudentSearch } from '../../../hooks/useBannerClassStudentSearch';

export interface TurmaAutocompleteProps {
  /** ID da turma selecionada; `null` quando nada. */
  readonly value: number | null;
  /** Rótulo da turma selecionada. */
  readonly selectedLabel: string;
  /** Notifica o novo ID, rótulo e marca (`null`/`''` ao limpar). */
  readonly onChange: (id: number | null, label: string, brandName?: string) => void;
  readonly label: string;
  readonly placeholder: string;
  /** Marcas selecionadas no filtro; vazio considera todas. */
  readonly brandIds?: readonly number[];
  /** Restringe a busca às turmas ativas. */
  readonly activeOnly?: boolean;
}

/**
 * Autocomplete de Turma do bloco "Turmas" do formulário de Banner.
 *
 * Busca em `GET /api/v1/banner/class-students` (ver
 * {@link useBannerClassStudentSearch}), respeitando os filtros de marca e de
 * turmas ativas, e devolve também a marca da turma escolhida para a lista de
 * vinculadas exibir.
 */
export function TurmaAutocomplete({
  value,
  selectedLabel,
  onChange,
  label,
  placeholder,
  brandIds = [],
  activeOnly = true,
}: TurmaAutocompleteProps) {
  const [search, setSearch] = useState('');
  const { options, isQuerySuccess } = useBannerClassStudentSearch(search, brandIds, activeOnly);

  return (
    <LookupAutocompleteView
      label={label}
      placeholder={placeholder}
      value={value !== null ? String(value) : ''}
      selectedLabel={selectedLabel}
      options={options.map(option => ({ id: option.id, label: option.label }))}
      isQuerySuccess={isQuerySuccess}
      search={search}
      onSearchChange={setSearch}
      onSelect={(id, itemLabel) => {
        const picked = options.find(option => String(option.id) === id);
        onChange(id ? Number(id) : null, itemLabel, picked?.brandName);
        setSearch('');
      }}
      onClear={() => {
        onChange(null, '');
        setSearch('');
      }}
    />
  );
}
